import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './hooks/useAuth';
import { SpeechSynthesisProvider } from './hooks/useSpeechSynthesis';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import { Chat } from './components/Chat';

// Lazy-loaded routes — admin pages and DocumentsPage are not on the
// critical path for the typical TECH user, so we defer their JS until
// the user navigates to them. recharts (~200KB) ships inside the
// AdminPageFinal chunk; the four admin chunks together drop ~30% off
// the main bundle.
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const AdminPageFinal = lazy(() => import('./pages/AdminPageFinal'));
const AllowedDomainsPage = lazy(() => import('./pages/admin/AllowedDomainsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const DeploysPage = lazy(() => import('./pages/admin/DeploysPage'));

const RouteFallback: React.FC = () => (
  <div className="loading-screen">
    <div className="text-center">
      <div className="loading-spinner mx-auto mb-4" />
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <SpeechSynthesisProvider>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/*
            /auth/callback must live OUTSIDE ProtectedRoute — the user
            is not authenticated yet when the Entra redirect lands here.
            ProtectedRoute would bounce them back to /login before the
            one-time app code could be consumed.
          */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/chat" element={<Chat />} />
                      <Route path="/chat/:conversationId" element={<Chat />} />
                      <Route path="/documents" element={<DocumentsPage />} />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute adminOnly>
                            <AdminPageFinal />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/allowed-domains"
                        element={
                          <ProtectedRoute userAdminOnly>
                            <AllowedDomainsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/users"
                        element={
                          <ProtectedRoute userAdminOnly>
                            <UsersPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/deploys"
                        element={
                          <ProtectedRoute userAdminOnly>
                            <DeploysPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="*" element={<Navigate to="/chat" replace />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </SpeechSynthesisProvider>
    </AuthProvider>
  );
}

export default App;
